<script setup lang="ts">
import { toRef, watch } from "vue";
import { useGetSectionFindings } from "@/stores/queries";
import type { Finding } from "@/types/findings.types";
import FindingsTable from "./FindingsTable.vue";
import DRBtn from "@/components/UI/DRBtn.vue";

const props = defineProps<{
  sectionId: number;
  cataloguePartId?: number;
  diagnosticId: number;
  reportId: number;
  hasChildren?: boolean;
}>();

const emit = defineEmits<{
  (e: "edit", finding: Finding): void;
  (e: "count", count: number): void;
  (e: "create"): void;
}>();

const sectionIdRef = toRef(props, "sectionId");
const { data: findings } = useGetSectionFindings(sectionIdRef);

// Emit count when findings change
watch(
  findings,
  (newFindings) => {
    emit("count", newFindings?.length ?? 0);
  },
  { immediate: true },
);
</script>

<template>
  <div
    v-if="findings?.length || !props.hasChildren"
    class="bordered shadow-1 rounded bg-white overflow-hidden"
  >
    <!-- Empty state: only show if section has no children -->
    <div
      v-if="!findings?.length"
      class="empty-state d-flex flex-column align-items-center justify-content-center py-4"
    >
      <span class="text-muted small mb-3">{{
        $t("report:findings.noFindings")
      }}</span>
      <DRBtn
        variant="outline"
        size="sm"
        icon="bi-plus-lg"
        :label="$t('report:findings.addFinding')"
        @click="emit('create')"
      />
    </div>

    <!-- Findings table -->
    <FindingsTable
      v-else
      :findings="findings"
      :diagnostic-id="diagnosticId"
      :report-id="reportId"
      :section-id="sectionId"
      show-numbering
      show-add-button
      @edit="(finding) => emit('edit', finding)"
      @create="emit('create')"
    />
  </div>
</template>

<style lang="scss" scoped>
.empty-state {
  min-height: 100px;
}
</style>
